import { GGOBaseComponent, Translate } from '@galigeo-store/ui-lib';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { PUBSUB_TOPICS, publish } from '../../state/pubsub.js';
import './items/divider.ts';
import './items/footer.component.ts';
import './items/item.component.ts';

@customElement('left-panel')
export class LeftPanel extends GGOBaseComponent {
  @property({ type: Boolean, reflect: true }) open = false;
  @property({ type: Array }) elements: Array<any> = [];
  @property({ type: String }) activeElement = '';

  @state() private delayedOpen = false;
  @state() private showBottomScroller = false;
  @state() private showTopScroller = false;

  private openTimeout: number | undefined;
  @property({ type: Object }) otherItem = {
    id: 'others',
    icon: './svg/icons.svg#others',
    label: `${Translate.get('others')}`,
  };
  static get styles() {
    return [
      css`
        :host {
          display: block;
          position: relative;
          height: 100vh;
          overflow: hidden;
          width: 56px;
          transition: width 0.3s ease-in-out;
          cursor: url('./svg/expand32.svg'), e-resize;
        }
        :host([open]) {
          width: 270px;
          cursor: url('./svg/collapse32.svg'), w-resize;
        }
        .panel {
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          background-color: #03234d;
          color: #fff;
          overflow: hidden;
        }
        .panel-header {
          padding: 12px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-shrink: 0;
          cursor: pointer;
        }
        .panel-header img {
          height: 2rem;
          margin: 0; /* Remove unnecessary margins */
        }
        .panel-content {
          flex-grow: 1;
          overflow-y: auto; /* Permet le défilement vertical */
          overflow-x: hidden; /* Masque le défilement horizontal */
          scrollbar-width: none; /* Masque la barre de défilement dans les navigateurs modernes */
          -ms-overflow-style: none; /* Masque la barre de défilement dans Internet Explorer */
          position: relative;
        }
        .scroller-button {
          position: sticky;
          bottom: 0px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          background-color: #03234d;
        }
        .hidden {
          display: none;
        }
        .scroller-button.top {
          position: sticky;
          top: 0;
          z-index: 1;
        }
        .scroller-button button {
          background:none;
          border:none;
          cursor:pointer;
          width: 100%;
        }
        .scroller-button button:hover {
          background: rgba(255, 255, 255, 0.1);
        }
      `,
    ];
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('open')) {
      this.handleOpenChange();
      this.checkPanelContentOverflow();
    }
  }

  handleOpenChange() {
    if (this.openTimeout) {
      clearTimeout(this.openTimeout);
      this.openTimeout = undefined;
    }

    if (this.open) {
      this.delayedOpen = true;
    } else {
      this.openTimeout = window.setTimeout(() => {
        this.delayedOpen = false;
        this.openTimeout = undefined;
      }, 300);
    }
  }

  protected firstUpdated() {
    this.checkPanelContentOverflow();
    const panelContent = this.shadowRoot?.querySelector('.panel-content');
    if (panelContent) {
      panelContent.addEventListener('scroll', () => this.updateScrollerVisibility());
    }
    // Also check on resize
    window.addEventListener('resize', () => this.updateScrollerVisibility());
  }

  private updateScrollerVisibility() {
    const panelContent = this.shadowRoot?.querySelector('.panel-content');
    if (panelContent) {
      const atBottom = panelContent.scrollTop + panelContent.clientHeight >= panelContent.scrollHeight - 1;
      const atTop = panelContent.scrollTop <= 1;
      this.showBottomScroller = !atBottom && panelContent.scrollHeight > panelContent.clientHeight;
      this.showTopScroller = !atTop && panelContent.scrollHeight > panelContent.clientHeight;
    }
  }

  private checkPanelContentOverflow() {
    const panelContent = this.shadowRoot?.querySelector('.panel-content');
    if (panelContent) {
      this.showBottomScroller = panelContent.scrollHeight > panelContent.clientHeight;
    }
  }

  private scrollPanelContentDown() {
    const panelContent = this.shadowRoot?.querySelector('.panel-content');
    if (panelContent) {
      panelContent.scrollBy({ top: 250, behavior: 'smooth' });
    }
  }

  private scrollPanelContentUp() {
    const panelContent = this.shadowRoot?.querySelector('.panel-content');
    if (panelContent) {
      panelContent.scrollBy({ top: -250, behavior: 'smooth' });
      setTimeout(() => this.updateScrollerVisibility(), 250);
    }
  }

  goHome() {
    this.activeElement = '';
    publish(PUBSUB_TOPICS.GO_HOME)
  }

  render() {
    this.otherItem = { ...this.otherItem, label: Translate.get('others') };

    const hasContent = this.elements.filter(
      (element) =>
        element.id === 'others' || element.type || element.maps?.length > 0,
    );
    return html`
      <div class="panel ${this.delayedOpen ? 'open' : ''}">
        <div
          class="panel-header"
          @click="${() => this.goHome()}"
        >
          ${this.delayedOpen
        ? html`<img src="./svg/logo-galigeo-big.svg" alt="Logo" />`
        : html`<img src="./svg/logo-galigeo-icon.svg" alt="Logo" />`}
        </div>
        <div class="panel-content">
          <div class="scroller-button top ${this.showTopScroller && this.open ? '' : 'hidden'}">
            <button @click="${this.scrollPanelContentUp}">
              <ggo-icon icon="utility:chevronup" color="#ffffff"></ggo-icon>
            </button>
          </div>
          ${hasContent.map(
          (element) => html`
              <panel-item
                .element="${element}"
                .panelOpen="${this.delayedOpen}"
                .active="${this.activeElement === element.id}"
                .isSelected="${this.activeElement === element.id}"
              ></panel-item>
            `,
        )}
          ${hasContent.length > 0
        ? html`<panel-divider></panel-divider>`
        : nothing}
          <panel-item
            .element="${this.otherItem}"
            .panelOpen="${this.delayedOpen}"
            .active="${this.activeElement === this.otherItem.id}"
            .isSelected="${this.activeElement === this.otherItem.id}"
          ></panel-item>
          <div class="scroller-button ${this.showBottomScroller && this.open ? '' : 'hidden'}">
            <button @click="${this.scrollPanelContentDown}">
              <ggo-icon icon="utility:chevrondown" color="#ffffff"></ggo-icon>
            </button>
          </div>
        </div>
        <div class="panel-footer">
          <panel-footer .panelOpen="${this.open}" .activeElement="${this.activeElement}"></panel-footer>
        </div>
      </div>
    `;
  }
}
